// src/pages/PaymentDispatchHistory.jsx
import React, { useState, useEffect } from "react";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { db } from "../services/firebase";
import { ChevronRight } from "lucide-react";
import Layout from "../component/Layout";
import ProductImage from "../components/ProductImage";

// Chevron Component

// Chevron Component
const Chevron = ({ open }) => (
  <ChevronRight
    size={16}
    style={{
      transition: "transform 0.15s ease",
      transform: open ? "rotate(90deg)" : "rotate(0deg)",
      color: "#475569",
    }}
  />
);

// Expandable Orders Table Component
const ExpandableOrdersTable = ({ orders }) => {
  const [expandedOrders, setExpandedOrders] = useState({});

  const toggleOrder = (orderId) => {
    setExpandedOrders((prev) => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "—";
    const date =
      timestamp instanceof Date
        ? timestamp
        : timestamp?.toDate
          ? timestamp.toDate()
          : new Date(timestamp);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const inr = (n) =>
    `₹${Number(n || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const num = (val) => {
    if (val === null || val === undefined) return 0;
    const n = Number(val);
    return isNaN(n) ? 0 : n;
  };

  // Calculate size-level details
  const getSizeDetails = (order) => {
    const sizeDetails = [];
    const orderDetail = order.orderDetail || {};

    Object.entries(orderDetail).forEach(([size, variant]) => {
      const qty = num(variant.quantity || 0);
      const netWt = num(variant.netWt || order.netWt || 0);
      const grossWt = num(variant.grossWt || order.grossWt || 0);
      const specWt = num(variant.specificationWt || order.specificationWt || 0);

      // Calculate fine weight for this size
      const purity = num(order.purity || 92);
      const wastagePercent = num(order.wastagePercent || order.wastage || 0);
      const fineWt = (netWt * qty * (purity + wastagePercent)) / 100;

      // Calculate MC costs
      const setMc = num(order.setMakingCharges || order.setMc || 0);
      const gramMc = num(order.gramMakingCharges || order.gramMc || 0);
      const specMc = num(order.specificationMakingCharges || order.specMc || 0);

      const setMcTotal = setMc * qty;
      const gramMcTotal = netWt * qty * gramMc;
      const specMcTotal = specWt * qty * specMc;
      const mcTotal = setMcTotal + gramMcTotal + specMcTotal;

      // Calculate stamp
      const stampCost = num(order.stampCharges || 0);
      const stampTotal = stampCost * qty;

      // Calculate amount
      const amount = mcTotal + stampTotal;

      sizeDetails.push({
        size,
        qty,
        grossWt: grossWt * qty,
        netWt: netWt * qty,
        specWt: specWt * qty,
        fineWt,
        mcTotal,
        mcPerGram: gramMc,
        stampTotal,
        stampCost,
        amount,
      });
    });

    return sizeDetails;
  };

  const thStyle = {
    border: "1px solid #e5e7eb",
    padding: "8px 10px",
    textAlign: "left",
    fontSize: "11px",
    fontWeight: "700",
    backgroundColor: "#f3f4f6",
    color: "#4b5563",
    position: "sticky",
    top: 0,
    zIndex: 1,
  };

  const tdStyle = {
    border: "1px solid #e5e7eb",
    padding: "8px 10px",
    fontSize: "12px",
    color: "#374151",
  };

  const calculateOrderTotals = (order) => {
    const orderDetail = order.orderDetail || {};
    let totalQty = 0;
    let totalGross = 0;
    let totalNet = 0;
    let totalSpec = 0;
    let totalFine = 0;
    let totalMc = 0;
    let totalStamp = 0;

    Object.values(orderDetail).forEach((variant) => {
      const qty = num(variant.quantity || 0);
      const netWt = num(variant.netWt || order.netWt || 0);
      const grossWt = num(variant.grossWt || order.grossWt || 0);
      const specWt = num(variant.specificationWt || order.specificationWt || 0);

      totalQty += qty;
      totalGross += grossWt * qty;
      totalNet += netWt * qty;
      totalSpec += specWt * qty;

      const purity = num(order.purity || 92);
      const wastagePercent = num(order.wastagePercent || order.wastage || 0);
      totalFine += (netWt * qty * (purity + wastagePercent)) / 100;

      const setMc = num(order.setMakingCharges || order.setMc || 0);
      const gramMc = num(order.gramMakingCharges || order.gramMc || 0);
      const specMc = num(order.specificationMakingCharges || order.specMc || 0);
      totalMc += setMc * qty + netWt * qty * gramMc + specWt * qty * specMc;

      const stampCost = num(order.stampCharges || 0);
      totalStamp += stampCost * qty;
    });

    return {
      totalQty,
      totalGross,
      totalNet,
      totalSpec,
      totalFine,
      totalMc,
      totalStamp,
      totalAmount: totalMc + totalStamp,
    };
  };

  return (
    <div style={{ marginTop: "12px" }}>
      <h4
        style={{
          margin: "0 0 12px 0",
          fontSize: "14px",
          color: "#6b7280",
          fontWeight: "600",
        }}
      >
        Orders in this Dispatch ({orders.length}):
      </h4>
      <div
        style={{
          overflowX: "auto",
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}
        >
          <colgroup>
            <col style={{ width: "36px" }} />
            <col style={{ width: "48px" }} />
            <col style={{ minWidth: "140px" }} />
            <col style={{ width: "64px" }} />
            <col style={{ width: "90px" }} />
            <col style={{ width: "90px" }} />
            <col style={{ width: "90px" }} />
            <col style={{ width: "90px" }} />
            <col style={{ width: "100px" }} />
            <col style={{ width: "120px" }} />
            <col style={{ width: "100px" }} />
            <col style={{ minWidth: "130px" }} />
          </colgroup>
          <thead>
            <tr>
              <th style={thStyle}></th>
              <th style={thStyle}>Img</th>
              <th style={{ ...thStyle, textAlign: "left" }}>
                Product / Order ID
              </th>
              <th style={{ ...thStyle, textAlign: "right" }}>Qty</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Gross Wt (g)</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Net Wt (g)</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Spec Wt (g)</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Fine Wt (g)</th>
              <th style={{ ...thStyle, textAlign: "right" }}>MC Total</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Stamp Total</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Amount</th>
              <th style={{ ...thStyle, textAlign: "left" }}>Order Time</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => {
              const totals = calculateOrderTotals(order);
              const isExpanded = expandedOrders[order.id];
              const sizeDetails = getSizeDetails(order);

              return (
                <React.Fragment key={order.id}>
                  {/* Main Order Row */}
                  <tr
                    onClick={() => toggleOrder(order.id)}
                    style={{
                      backgroundColor: isExpanded ? "#eef2ff" : "#ffffff",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      if (!isExpanded)
                        e.currentTarget.style.backgroundColor = "#f1f5f9";
                    }}
                    onMouseLeave={(e) => {
                      if (!isExpanded)
                        e.currentTarget.style.backgroundColor = "#ffffff";
                    }}
                  >
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      <Chevron open={isExpanded} />
                    </td>
                    <td
                      style={{ ...tdStyle, textAlign: "center" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ProductImage
                        images={order.images}
                        productName={order.productName || order.product}
                        label={`${order.productName || "Product"} · ${(order.docId || order.id || "").slice(0, 8)}`}
                        size={36}
                      />
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600, fontSize: "13px" }}>
                        {order.productName || order.product || "—"}
                      </div>
                      <div
                        style={{
                          fontSize: "10px",
                          color: "#6b7280",
                          marginTop: "2px",
                          fontFamily: "monospace",
                        }}
                      >
                        {order.id.substring(0, 12)}...
                      </div>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      {totals.totalQty || "—"}
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {totals.totalGross.toFixed(3)}
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {totals.totalNet.toFixed(3)}
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {totals.totalSpec.toFixed(3)}
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {totals.totalFine.toFixed(3)}
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {inr(totals.totalMc)}
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {inr(totals.totalStamp)}
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        textAlign: "right",
                        fontWeight: 700,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {inr(totals.totalAmount)}
                    </td>
                    <td
                      style={{ ...tdStyle, fontSize: "11px", color: "#475569" }}
                    >
                      {formatTimestamp(order.orderTime)}
                    </td>
                  </tr>

                  {/* Size Details Rows */}
                  {isExpanded && sizeDetails.length > 0 && (
                    <tr>
                      <td colSpan={12} style={{ padding: 0, border: "none" }}>
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            tableLayout: "fixed",
                          }}
                        >
                          <colgroup>
                            <col style={{ width: "36px" }} />
                            <col style={{ width: "48px" }} />
                            <col style={{ minWidth: "140px" }} />
                            <col style={{ width: "64px" }} />
                            <col style={{ width: "90px" }} />
                            <col style={{ width: "90px" }} />
                            <col style={{ width: "90px" }} />
                            <col style={{ width: "90px" }} />
                            <col style={{ width: "100px" }} />
                            <col style={{ width: "120px" }} />
                            <col style={{ width: "100px" }} />
                            <col style={{ minWidth: "130px" }} />
                          </colgroup>
                          <thead>
                            <tr style={{ backgroundColor: "#eef0fa" }}>
                              <th style={{ ...thStyle, fontSize: "10px" }}></th>
                              <th style={{ ...thStyle, fontSize: "10px" }}></th>
                              <th
                                style={{
                                  ...thStyle,
                                  fontSize: "10px",
                                  textAlign: "left",
                                }}
                              >
                                Size
                              </th>
                              <th
                                style={{
                                  ...thStyle,
                                  fontSize: "10px",
                                  textAlign: "right",
                                }}
                              >
                                Qty
                              </th>
                              <th
                                style={{
                                  ...thStyle,
                                  fontSize: "10px",
                                  textAlign: "right",
                                }}
                              >
                                Gross (g)
                              </th>
                              <th
                                style={{
                                  ...thStyle,
                                  fontSize: "10px",
                                  textAlign: "right",
                                }}
                              >
                                Net (g)
                              </th>
                              <th
                                style={{
                                  ...thStyle,
                                  fontSize: "10px",
                                  textAlign: "right",
                                }}
                              >
                                Spec (g)
                              </th>
                              <th
                                style={{
                                  ...thStyle,
                                  fontSize: "10px",
                                  textAlign: "right",
                                }}
                              >
                                Fine (g)
                              </th>
                              <th
                                style={{
                                  ...thStyle,
                                  fontSize: "10px",
                                  textAlign: "right",
                                }}
                              >
                                <div
                                  style={{ fontSize: "9px", color: "#6b7280" }}
                                >
                                  MC (₹/g)
                                </div>
                                <div>MC Total</div>
                              </th>
                              <th
                                style={{
                                  ...thStyle,
                                  fontSize: "10px",
                                  textAlign: "right",
                                }}
                              >
                                <div
                                  style={{ fontSize: "9px", color: "#6b7280" }}
                                >
                                  Stamp (₹/set)
                                </div>
                                <div>Stamp Total</div>
                              </th>
                              <th
                                style={{
                                  ...thStyle,
                                  fontSize: "10px",
                                  textAlign: "right",
                                }}
                              >
                                Amount
                              </th>
                              <th style={{ ...thStyle, fontSize: "10px" }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {sizeDetails.map((detail, index) => (
                              <tr
                                key={index}
                                style={{ backgroundColor: "#f7f8ff" }}
                              >
                                <td
                                  style={{
                                    ...tdStyle,
                                    borderBottom: "1px solid #e8ebf4",
                                  }}
                                ></td>
                                <td
                                  style={{
                                    ...tdStyle,
                                    borderBottom: "1px solid #e8ebf4",
                                  }}
                                ></td>
                                <td
                                  style={{
                                    ...tdStyle,
                                    borderBottom: "1px solid #e8ebf4",
                                    fontWeight: 700,
                                  }}
                                >
                                  {detail.size}
                                </td>
                                <td
                                  style={{
                                    ...tdStyle,
                                    borderBottom: "1px solid #e8ebf4",
                                    textAlign: "right",
                                  }}
                                >
                                  {detail.qty}
                                </td>
                                <td
                                  style={{
                                    ...tdStyle,
                                    borderBottom: "1px solid #e8ebf4",
                                    textAlign: "right",
                                  }}
                                >
                                  {detail.grossWt.toFixed(3)}
                                </td>
                                <td
                                  style={{
                                    ...tdStyle,
                                    borderBottom: "1px solid #e8ebf4",
                                    textAlign: "right",
                                  }}
                                >
                                  {detail.netWt.toFixed(3)}
                                </td>
                                <td
                                  style={{
                                    ...tdStyle,
                                    borderBottom: "1px solid #e8ebf4",
                                    textAlign: "right",
                                  }}
                                >
                                  {detail.specWt.toFixed(3)}
                                </td>
                                <td
                                  style={{
                                    ...tdStyle,
                                    borderBottom: "1px solid #e8ebf4",
                                    textAlign: "right",
                                    fontVariantNumeric: "tabular-nums",
                                  }}
                                >
                                  {detail.fineWt.toFixed(3)}
                                </td>
                                <td
                                  style={{
                                    ...tdStyle,
                                    borderBottom: "1px solid #e8ebf4",
                                    textAlign: "right",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: "9px",
                                      color: "#6b7280",
                                    }}
                                  >
                                    ₹{detail.mcPerGram.toFixed(2)}/g
                                  </div>
                                  <div
                                    style={{
                                      fontVariantNumeric: "tabular-nums",
                                      fontWeight: 600,
                                      fontSize: "11px",
                                    }}
                                  >
                                    {inr(detail.mcTotal)}
                                  </div>
                                </td>
                                <td
                                  style={{
                                    ...tdStyle,
                                    borderBottom: "1px solid #e8ebf4",
                                    textAlign: "right",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: "9px",
                                      color: "#6b7280",
                                    }}
                                  >
                                    ₹{detail.stampCost.toFixed(2)}/set
                                  </div>
                                  <div
                                    style={{
                                      fontVariantNumeric: "tabular-nums",
                                      fontSize: "10px",
                                    }}
                                  >
                                    {detail.qty} × ₹
                                    {detail.stampCost.toFixed(2)} ={" "}
                                    {inr(detail.stampTotal)}
                                  </div>
                                </td>
                                <td
                                  style={{
                                    ...tdStyle,
                                    borderBottom: "1px solid #e8ebf4",
                                    textAlign: "right",
                                    fontWeight: 700,
                                    fontVariantNumeric: "tabular-nums",
                                  }}
                                >
                                  {inr(detail.amount)}
                                </td>
                                <td
                                  style={{
                                    ...tdStyle,
                                    borderBottom: "1px solid #e8ebf4",
                                  }}
                                ></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PaymentDispatchHistory = () => {
  const [dispatchHistory, setDispatchHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, PRE_PICKUP, WAREHOUSE
  const [dateFilter, setDateFilter] = useState("today"); // today, week, month, all
  const operatorId = localStorage.getItem("operatorId");

  useEffect(() => {
    if (!operatorId) return;
    fetchDispatchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operatorId, filter, dateFilter]);

  const fetchDispatchHistory = async () => {
    setLoading(true);
    try {
      // Query settlements collection for dispatched seller payments
      const settlementsQuery = query(
        collection(db, "settlements"),
        where("operatorId", "==", operatorId),
        where("sellerPaymentStatus", "in", [
          "dispatched",
          "verified",
          "pending",
        ]),
        orderBy("dispatchedToSellerAt", "desc"),
      );

      const settlementsSnapshot = await getDocs(settlementsQuery);
      const settlements = [];

      for (const docSnap of settlementsSnapshot.docs) {
        const settlement = { id: docSnap.id, ...docSnap.data() };

        // Get detailed order info for the settlement
        const orderIds = settlement.orderIds || [];
        if (orderIds.length > 0) {
          const ordersQuery = query(
            collection(db, "orderList"),
            where("__name__", "in", orderIds.slice(0, 10)), // Firestore 'in' limit is 10
          );
          const ordersSnapshot = await getDocs(ordersQuery);
          const orders = ordersSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          // Determine payment stage from orders
          const paymentStage =
            orders[0]?.sellerPaymentStage ||
            orders[0]?.paymentPreference ||
            "BUYER_PAYMENT";

          settlements.push({
            ...settlement,
            orders,
            paymentStage,
            dispatchedAt:
              settlement.dispatchedToSellerAt?.toDate() ||
              settlement.paymentDispatchedToSellerAt?.toDate() ||
              settlement.createdAt?.toDate(),
          });
        }
      }

      // Apply filters
      let filtered = settlements;

      // Stage filter
      if (filter !== "all") {
        filtered = filtered.filter((s) => s.paymentStage === filter);
      }

      // Date filter
      if (dateFilter !== "all") {
        const now = new Date();
        const startDate = new Date();

        if (dateFilter === "today") {
          startDate.setHours(0, 0, 0, 0);
        } else if (dateFilter === "week") {
          startDate.setDate(now.getDate() - 7);
        } else if (dateFilter === "month") {
          startDate.setMonth(now.getMonth() - 1);
        }

        filtered = filtered.filter(
          (s) => s.dispatchedAt && s.dispatchedAt >= startDate,
        );
      }

      setDispatchHistory(filtered);
    } catch (error) {
      console.error("Error fetching dispatch history:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    if (!(date instanceof Date)) date = new Date(date);
    return date.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStageLabel = (stage) => {
    const labels = {
      PRE_PICKUP: "📦 Pre-Pickup Payment",
      WAREHOUSE: "🏭 Warehouse Payment",
      BUYER_PAYMENT: "💰 Post-Delivery Payment",
    };
    return labels[stage] || stage;
  };

  const getStatusBadge = (status) => {
    const styles = {
      dispatched: { bg: "#fef3c7", color: "#92400e", text: "🚚 Dispatched" },
      verified: { bg: "#d1fae5", color: "#065f46", text: "✅ Verified" },
      pending: { bg: "#fee2e2", color: "#991b1b", text: "⏳ Pending" },
    };
    const style = styles[status] || styles.pending;
    return (
      <span
        style={{
          backgroundColor: style.bg,
          color: style.color,
          padding: "4px 12px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: "600",
        }}
      >
        {style.text}
      </span>
    );
  };

  const containerStyle = {
    padding: "20px",
    backgroundColor: "#f9fafb",
    minHeight: "100vh",
  };

  const headerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
    flexWrap: "wrap",
    gap: "16px",
  };

  const filterGroupStyle = {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  };

  const filterButtonStyle = (isActive) => ({
    padding: "8px 16px",
    border: `2px solid ${isActive ? "#3b82f6" : "#d1d5db"}`,
    backgroundColor: isActive ? "#3b82f6" : "#fff",
    color: isActive ? "#fff" : "#374151",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "500",
    fontSize: "14px",
    transition: "all 0.2s",
  });

  const cardStyle = {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "16px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e5e7eb",
  };

  const summaryRowStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    marginBottom: "16px",
    paddingBottom: "16px",
    borderBottom: "1px solid #e5e7eb",
  };

  const summaryItemStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  };

  const labelStyle = {
    fontSize: "12px",
    color: "#6b7280",
    fontWeight: "500",
    textTransform: "uppercase",
  };

  const valueStyle = {
    fontSize: "18px",
    fontWeight: "700",
    color: "#111827",
  };

  return (
    <Layout pageTitle="Payment Dispatch History">
      <div style={containerStyle}>
        {/* Header with Filters */}
        <div style={headerStyle}>
          <h2
            style={{
              margin: 0,
              fontSize: "24px",
              fontWeight: "700",
              color: "#111827",
            }}
          >
            💸 Payment Dispatch History
          </h2>

          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
            {/* Stage Filter */}
            <div style={filterGroupStyle}>
              <button
                style={filterButtonStyle(filter === "all")}
                onClick={() => setFilter("all")}
              >
                All Stages
              </button>
              <button
                style={filterButtonStyle(filter === "PRE_PICKUP")}
                onClick={() => setFilter("PRE_PICKUP")}
              >
                📦 Pre-Pickup
              </button>
              <button
                style={filterButtonStyle(filter === "WAREHOUSE")}
                onClick={() => setFilter("WAREHOUSE")}
              >
                🏭 Warehouse
              </button>
            </div>

            {/* Date Filter */}
            <div style={filterGroupStyle}>
              <button
                style={filterButtonStyle(dateFilter === "today")}
                onClick={() => setDateFilter("today")}
              >
                Today
              </button>
              <button
                style={filterButtonStyle(dateFilter === "week")}
                onClick={() => setDateFilter("week")}
              >
                This Week
              </button>
              <button
                style={filterButtonStyle(dateFilter === "month")}
                onClick={() => setDateFilter("month")}
              >
                This Month
              </button>
              <button
                style={filterButtonStyle(dateFilter === "all")}
                onClick={() => setDateFilter("all")}
              >
                All Time
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div
            style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}
          >
            Loading dispatch history...
          </div>
        )}

        {/* Empty State */}
        {!loading && dispatchHistory.length === 0 && (
          <div style={cardStyle}>
            <div
              style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}
            >
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>📭</div>
              <p style={{ margin: 0, fontSize: "16px" }}>
                No payment dispatches found for the selected filters.
              </p>
            </div>
          </div>
        )}

        {/* Dispatch History Cards */}
        {!loading &&
          dispatchHistory.map((settlement) => (
            <div key={settlement.id} style={cardStyle}>
              {/* Settlement Summary */}
              <div style={summaryRowStyle}>
                <div style={summaryItemStyle}>
                  <span style={labelStyle}>Payment Stage</span>
                  <span style={valueStyle}>
                    {getStageLabel(settlement.paymentStage)}
                  </span>
                </div>

                <div style={summaryItemStyle}>
                  <span style={labelStyle}>Seller</span>
                  <span style={valueStyle}>{settlement.sellerId || "N/A"}</span>
                </div>

                <div style={summaryItemStyle}>
                  <span style={labelStyle}>Total Amount</span>
                  <span style={valueStyle}>
                    ₹
                    {settlement.totalAmountDue?.toFixed(2) ||
                      settlement.totalAmount?.toFixed(2) ||
                      "0.00"}
                  </span>
                </div>

                <div style={summaryItemStyle}>
                  <span style={labelStyle}>Fine Weight</span>
                  <span style={valueStyle}>
                    {settlement.totalFineWeightDue?.toFixed(3) ||
                      settlement.totalFine?.toFixed(3) ||
                      "0.000"}
                    gm
                  </span>
                </div>

                <div style={summaryItemStyle}>
                  <span style={labelStyle}>Status</span>
                  <div style={{ marginTop: "4px" }}>
                    {getStatusBadge(
                      settlement.sellerPaymentStatus || "pending",
                    )}
                  </div>
                </div>

                <div style={summaryItemStyle}>
                  <span style={labelStyle}>Dispatched At</span>
                  <span style={{ ...valueStyle, fontSize: "14px" }}>
                    {formatDate(settlement.dispatchedAt)}
                  </span>
                </div>
              </div>

              {/* Payment Buckets */}
              {(settlement.rtgsBucket || settlement.metalBucket) && (
                <div
                  style={{
                    marginBottom: "16px",
                    paddingBottom: "16px",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <h4
                    style={{
                      margin: "0 0 12px 0",
                      fontSize: "14px",
                      color: "#6b7280",
                      fontWeight: "600",
                    }}
                  >
                    Payment Breakdown:
                  </h4>
                  <div
                    style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}
                  >
                    {settlement.rtgsBucket && (
                      <div>
                        <span
                          style={{
                            ...labelStyle,
                            display: "block",
                            marginBottom: "4px",
                          }}
                        >
                          💳 RTGS Payment
                        </span>
                        <span
                          style={{
                            fontSize: "16px",
                            fontWeight: "600",
                            color: "#059669",
                          }}
                        >
                          ₹{settlement.rtgsBucket.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {settlement.metalBucket && (
                      <div>
                        <span
                          style={{
                            ...labelStyle,
                            display: "block",
                            marginBottom: "4px",
                          }}
                        >
                          🥇 Metal Payment
                        </span>
                        <span
                          style={{
                            fontSize: "16px",
                            fontWeight: "600",
                            color: "#d97706",
                          }}
                        >
                          {settlement.metalBucket.toFixed(3)}gm
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* JRE Information */}
              {settlement.jreId && (
                <div
                  style={{
                    marginBottom: "16px",
                    padding: "12px",
                    backgroundColor: "#f3f4f6",
                    borderRadius: "8px",
                  }}
                >
                  <span style={{ ...labelStyle, marginRight: "8px" }}>
                    🚚 Delivered by JRE:
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#111827",
                    }}
                  >
                    {settlement.jreId}
                  </span>
                </div>
              )}

              {/* Orders Table - expandable with size details */}
              {settlement.orders && settlement.orders.length > 0 && (
                <ExpandableOrdersTable orders={settlement.orders} />
              )}

              {/* Settlement ID */}
              <div
                style={{
                  marginTop: "16px",
                  paddingTop: "16px",
                  borderTop: "1px solid #e5e7eb",
                }}
              >
                <span style={{ ...labelStyle, marginRight: "8px" }}>
                  Settlement ID:
                </span>
                <span
                  style={{
                    fontFamily: "monospace",
                    fontSize: "12px",
                    color: "#6b7280",
                  }}
                >
                  {settlement.sellerSettlementId || settlement.id}
                </span>
              </div>
            </div>
          ))}
      </div>
    </Layout>
  );
};

export default PaymentDispatchHistory;
